<script setup>
/**
 * A picture that opens (picture-caption DESIGN.md, revision 1, 2026-10-30).
 *
 * Everywhere the caption does not sit under the picture (the gift's front
 * door, the shared lesson page, Community cards, profile tiles, the Mirror),
 * the picture is a button: tap it and it opens full size in PictureView, its
 * caption over the picture's foot.
 *
 * - The affordance, never a text label: a zoom-in cursor; a small expand mark
 *   top-right (clear of the Mystic's mark bottom-left and of a note tucked
 *   over the bottom edge), always faintly there since a phone has no hover;
 *   one faint glint the first time the picture comes into view (none with
 *   reduced motion).
 * - Named "Open the picture: {caption}"; the caption is the picture's alt.
 * - Its tap is its own (`.stop`): never the card's, the tile's or the page's.
 * - Closing returns focus to it.
 *
 * Fills its box (the host sizes it); `small` is the Mirror tiles' 24px mark.
 * Emits `error` when the picture fails, so the host can fall back.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { openLabel } from '@/utils/picture-caption'
import PictureView from './PictureView.vue'

const props = defineProps({
  src: { type: String, required: true },
  caption: { type: String, default: '' },
  small: { type: Boolean, default: false },
  // The button's verb: "Open" (default) or the lesson plate's "Enlarge".
  verb: { type: String, default: 'Open' },
  // The img's own test id (a host's existing one, e.g. "public-letter-image").
  imgTestid: { type: String, default: 'picture-open-image' },
})
const emit = defineEmits(['error', 'load'])

const btn = ref(null)
const open = ref(false)
const seen = ref(false)
const label = computed(() => openLabel(props.caption, props.verb))

function show() { open.value = true }
function close() {
  open.value = false
  nextTick(() => { const b = btn.value; if (b) { try { b.focus({ preventScroll: true }) } catch (e) { b.focus() } } })
}

// The glint: once, the first time the picture comes into view.
let io = null
onMounted(() => {
  if (typeof window === 'undefined' || !window.IntersectionObserver || !btn.value) return
  io = new window.IntersectionObserver((entries) => {
    if (!entries.some((e) => e.isIntersecting)) return
    seen.value = true
    if (io) io.disconnect()
    io = null
  }, { threshold: 0.4 })
  io.observe(btn.value)
})
onBeforeUnmount(() => { if (io) io.disconnect(); io = null })
</script>

<template>
  <button ref="btn" type="button" class="po" :class="{ sm: small, seen }" :aria-label="label" data-testid="picture-open"
          @click.stop="show">
    <img :src="src" :alt="caption" class="po-img" :data-testid="imgTestid" @error="emit('error', $event)" @load="emit('load', $event)" />
    <!-- A host's own shade over the picture, under the mark and the glint. -->
    <slot />
    <span class="po-mark" aria-hidden="true" data-testid="picture-open-mark">
      <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
        <path d="M9.5 2.5h4v4M6.5 13.5h-4v-4M13.5 2.5 9 7M2.5 13.5 7 9" />
      </svg>
    </span>
    <PictureView v-if="open" :src="src" :caption="caption" @close="close" />
  </button>
</template>

<style scoped>
.po {
  position: relative; display: block; width: 100%; height: 100%; padding: 0; margin: 0; border: 0; background: none;
  font: inherit; color: inherit; overflow: hidden; cursor: zoom-in; isolation: isolate; border-radius: inherit;
}
.po-img { display: block; width: 100%; height: 100%; object-fit: cover; }
.po-mark {
  position: absolute; top: 10px; right: 10px; z-index: 4; width: 30px; height: 30px; border-radius: 9px; display: grid; place-items: center;
  background: rgba(20, 9, 38, .42); box-shadow: 0 0 0 1px rgba(231, 201, 131, .35); color: #ffe9b8; opacity: .78; pointer-events: none;
  backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px); transition: opacity .2s, transform .2s;
}
.po-mark svg { width: 14px; height: 14px; }
.po:hover .po-mark, .po:focus-visible .po-mark { opacity: 1; transform: scale(1.06); }
.sm .po-mark { top: 7px; right: 7px; width: 24px; height: 24px; border-radius: 7px; }
.sm .po-mark svg { width: 11px; height: 11px; }
/* The glint: one faint light band, once. */
.po::after {
  content: ''; position: absolute; inset: -20% -60%; z-index: 3; pointer-events: none; opacity: 0;
  background: linear-gradient(115deg, transparent 42%, rgba(255, 240, 210, .26) 50%, transparent 58%); transform: translateX(-60%);
}
.po.seen::after { animation: po-glint 1.3s cubic-bezier(.3, .6, .3, 1) .5s 1 both; }
@keyframes po-glint { 0% { opacity: 1; transform: translateX(-60%); } 100% { opacity: 1; transform: translateX(60%); } }
.po:focus-visible { outline: 3px solid #e7c983; outline-offset: -3px; }
@media (prefers-reduced-motion: reduce) {
  .seen::after { animation: none; }
  .po-mark { transition: none; }
}
</style>
